// reviewmistakes.js
// Review Mistakes Component - Shows filtered incorrect questions

import React, { useState, useEffect } from 'react';
import API_BASE_URL from './config';
import { API_BASE, authFetch } from '../services/api.js';
import './quizreviewmistakes.css';
import CustomSelect from '../components/CustomSelect';

const ReviewMistakes = ({ studentId }) => {
    // Filter states
    const [subjects, setSubjects] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [topics, setTopics] = useState([]);
    const [subtopics, setSubtopics] = useState([]);
    
    const [selectedSubject, setSelectedSubject] = useState('');
    const [selectedChapter, setSelectedChapter] = useState('');
    const [selectedTopic, setSelectedTopic] = useState('');
    const [selectedSubtopic, setSelectedSubtopic] = useState('');
    const [attemptType, setAttemptType] = useState('all');
    
    // Data states
    const [incorrectQuestions, setIncorrectQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Load subjects on component mount
    useEffect(() => {
        loadSubjects();
    }, []);

    // Load dependent dropdowns
    useEffect(() => {
        if (selectedSubject) {
            loadChapters(selectedSubject);
            setSelectedChapter('');
            setSelectedTopic('');
            setSelectedSubtopic('');
        }
    }, [selectedSubject]);

    useEffect(() => {
        if (selectedChapter) {
            loadTopics(selectedChapter);
            setSelectedTopic('');
            setSelectedSubtopic('');
        }
    }, [selectedChapter]);

    useEffect(() => {
        if (selectedTopic) {
            loadSubtopics(selectedTopic);
            setSelectedSubtopic('');
        }
    }, [selectedTopic]);

    // Load incorrect questions when filters change
    useEffect(() => {
        if (selectedSubject) {
            // Only load if topic is valid for selected chapter (or empty)
            if (selectedChapter && selectedTopic) {
                // Check if selected topic exists in current topics list
                const topicExists = topics.some(t => t.topic_id == selectedTopic);
                if (!topicExists) {
                    return; // Don't load if topic is invalid for current chapter
                }
            }
            
            // Only load if subtopic is valid for selected topic (or empty)
            if (selectedTopic && selectedSubtopic) {
                // Check if selected subtopic exists in current subtopics list
                const subtopicExists = subtopics.some(st => st.subtopic_id == selectedSubtopic);
                if (!subtopicExists) {
                    return; // Don't load if subtopic is invalid for current topic
                }
            }
            
            loadIncorrectQuestions();
        }
    }, [selectedSubject, selectedChapter, selectedTopic, selectedSubtopic, attemptType, topics, subtopics]);

    const loadSubjects = async () => {
        try {
            const response = await authFetch(`${API_BASE_URL}/quiz/report/subjects`);
            const data = await response.json();
            setSubjects(data);
        } catch (err) {
            console.error('Error loading subjects:', err);
        }
    };

    const loadChapters = async (subjectId) => {
        try {
            const response = await authFetch(`${API_BASE_URL}/quiz/report/chapters/subject/${subjectId}`);
            const data = await response.json();
            setChapters(data);
        } catch (err) {
            console.error('Error loading chapters:', err);
            setChapters([]);
        }
    };

    const loadTopics = async (chapterId) => {
        try {
            const response = await authFetch(`${API_BASE_URL}/quiz/report/topics/chapter/${chapterId}`);
            const data = await response.json();
            setTopics(data);
        } catch (err) {
            console.error('Error loading topics:', err);
            setTopics([]);
        }
    };

    const loadSubtopics = async (topicId) => {
        try {
            const response = await authFetch(`${API_BASE_URL}/quiz/report/subtopics/topic/${topicId}`);
            const data = await response.json();
            setSubtopics(data);
        } catch (err) {
            console.error('Error loading subtopics:', err);
            setSubtopics([]);
        }
    };

    const loadIncorrectQuestions = async () => {
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                subject_id: selectedSubject,
                attempt_type: attemptType
            });

            if (selectedChapter) params.append('chapter_id', selectedChapter);
            if (selectedTopic) params.append('topic_id', selectedTopic);
            if (selectedSubtopic) params.append('subtopic_id', selectedSubtopic);

            const response = await authFetch(
                `${API_BASE_URL}/review-mistakes/student/${studentId}/incorrect-questions?${params}`
            );

            if (!response.ok) throw new Error('Failed to load incorrect questions');

            const data = await response.json();
            setIncorrectQuestions(data);
        } catch (err) {
            setError('Failed to load questions. Please try again.');
            console.error('Error loading incorrect questions:', err);
        } finally {
            setLoading(false);
        }
    };

    const groupQuestionsByTopic = () => {
        const grouped = {};
        incorrectQuestions.forEach(q => {
            const key = 'General'; // Can be enhanced to use actual topic names
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(q);
        });
        return grouped;
    };

    return (
        <div className="review-mistakes-container">
            <div className="mistakes-header">
                <h3>Review Your Mistakes</h3>
                <p>Focus on these questions to improve your performance</p>
            </div>

            {/* Filter Section */}
            <div className="mistakes-filters">
                <div className="filter-row">
                    <div className="filter-group">
                        <label htmlFor="subject">Subject *</label>
                        <CustomSelect
                            value={selectedSubject}
                            onChange={(e) => setSelectedSubject(e.target.value)}
                            className="filter-select"
                            placeholder="Select Subject"
                            options={subjects.map(s => ({ value: s.subject_id, label: s.name }))}
                        />
                    </div>

                    <div className="filter-group">
                        <label htmlFor="chapter">Chapter</label>
                        <CustomSelect
                            value={selectedChapter}
                            onChange={(e) => setSelectedChapter(e.target.value)}
                            className="filter-select"
                            disabled={!selectedSubject}
                            placeholder="All Chapters"
                            options={[
                                { value: '', label: 'All Chapters' },
                                ...chapters.map(c => ({ value: c.chapter_id, label: c.name }))
                            ]}
                        />
                    </div>

                    <div className="filter-group">
                        <label htmlFor="topic">Topic</label>
                        <CustomSelect
                            value={selectedTopic}
                            onChange={(e) => setSelectedTopic(e.target.value)}
                            className="filter-select"
                            disabled={!selectedChapter}
                            placeholder="All Topics"
                            options={[
                                { value: '', label: 'All Topics' },
                                ...topics.map(t => ({ value: t.topic_id, label: t.name }))
                            ]}
                        />
                    </div>

                    <div className="filter-group">
                        <label htmlFor="subtopic">Subtopic</label>
                        <CustomSelect
                            value={selectedSubtopic}
                            onChange={(e) => setSelectedSubtopic(e.target.value)}
                            className="filter-select"
                            disabled={!selectedTopic}
                            placeholder="All Subtopics"
                            options={[
                                { value: '', label: 'All Subtopics' },
                                ...subtopics.map(s => ({ value: s.subtopic_id, label: s.name }))
                            ]}
                        />
                    </div>

                    <div className="filter-group">
                        <label htmlFor="attemptType">Attempt Type</label>
                        <CustomSelect
                            value={attemptType}
                            onChange={(e) => setAttemptType(e.target.value)}
                            className="filter-select"
                            options={[
                                { value: 'all', label: 'All Attempts' },
                                { value: 'latest', label: 'Latest Attempt Only' },
                            ]}
                        />
                    </div>
                </div>
            </div>

            {/* Content Section */}
            {loading && (
                <div className="loading-spinner">Loading questions...</div>
            )}

            {error && (
                <div className="error-message">{error}</div>
            )}

            {!loading && !error && !selectedSubject && (
                <div className="no-selection">
                    <p>Please select a subject to view your mistakes</p>
                </div>
            )}

            {!loading && !error && selectedSubject && incorrectQuestions.length === 0 && (
                <div className="no-mistakes">
                    <div className="success-icon">🎉</div>
                    <h4>Great job!</h4>
                    <p>You haven't made any mistakes in the selected filters. Keep up the excellent work!</p>
                </div>
            )}

            {!loading && !error && incorrectQuestions.length > 0 && (
                <div className="incorrect-questions">
                    {Object.entries(groupQuestionsByTopic()).map(([topic, questions]) => (
                        <div key={topic} className="topic-group">
                            <h4 className="topic-title">{topic} ({questions.length} questions)</h4>
                            <div className="questions-grid">
                                {questions.map((question, index) => (
                                    <div key={index} className="incorrect-question-card">
                                        <div className="quiz-info">
                                            <span className="quiz-badge">📝 {question.quiz_title}</span>
                                        <span className="question-date">
                                            📅 {new Date(question.attempt_date).toLocaleDateString()}
                                        </span>    
                                        </div>
                                        <div className="question-text">
                                            <strong>Q:</strong> {question.question_text}
                                        </div>
                                        <div className="options-display">
                                            {['A', 'B', 'C', 'D'].map(option => {
                                                const optionText = question[`option${option}`];
                                                if (!optionText) return null;
                                                
                                                const isCorrect = question.correct_answer === option;
                                                const isStudentAnswer = question.student_answer === option;
                                                
                                                return (
                                                    <div 
                                                        key={option} 
                                                        className={`option-item ${isCorrect ? 'correct-option' : ''} ${isStudentAnswer && !isCorrect ? 'wrong-option' : ''}`}
                                                    >
                                                        <span className="option-letter">{option}.</span>
                                                        <span className="option-text">{optionText}</span>
                                                        {isCorrect && <span className="correct-badge">✓ Correct</span>}
                                                        {isStudentAnswer && !isCorrect && <span className="wrong-badge">✗ Your Answer</span>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {question.explanation && (
                                            <div className="explanation">
                                                <strong>💡 Explanation:</strong> {question.explanation}
                                            </div>
                                        )}
                                        <div className="question-footer">
                                            <span className="question-date">
                                                📅 {new Date(question.attempt_date).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ReviewMistakes;